import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Twitter, Mail, MapPin, Phone } from 'lucide-react';
import { publicApi } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

export default function Footer() {
  const { t } = useLanguage();
  const [contactSettings, setContactSettings] = useState({
    contact_email: 'contact@joeycyan.com',
    contact_phone: '+33 7 58 90 70 21',
    whatsapp_number: '+33758907021'
  });

  const [socialLinks, setSocialLinks] = useState({
    facebook_url: '',
    instagram_url: '',
    twitter_url: '',
    linkedin_url: '',
    youtube_url: '',
    tiktok_url: ''
  });

  useEffect(() => {
    const fetchContactSettings = async () => {
      try { const { data } = await publicApi.get('/settings/contact'); setContactSettings(data); } catch (error) { console.error('Error loading contact settings:', error); }
    };
    const fetchSocialLinks = async () => {
      try { const { data } = await publicApi.get('/settings/social'); setSocialLinks(data); } catch (error) { console.error('Error loading social links:', error); }
    };
    fetchContactSettings();
    fetchSocialLinks();
  }, []);

  return (
    <footer className="bg-gray-900 text-gray-300 mt-20">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
          {/* About */}
          <div>
            <h3 className="text-white font-black text-2xl mb-4">Joey Cyan</h3>
            <p className="text-gray-400 text-sm leading-relaxed mb-4">{t('footer.description')}</p>
            <div className="flex gap-3">
              {socialLinks.facebook_url && socialLinks.facebook_url.trim() && (
                <a href={socialLinks.facebook_url} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-gray-800 hover:bg-[#00aaff] flex items-center justify-center transition-colors"><Facebook size={18} /></a>
              )}
              {socialLinks.instagram_url && socialLinks.instagram_url.trim() && (
                <a href={socialLinks.instagram_url} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-gray-800 hover:bg-[#00aaff] flex items-center justify-center transition-colors"><Instagram size={18} /></a>
              )}
              {socialLinks.twitter_url && socialLinks.twitter_url.trim() && (
                <a href={socialLinks.twitter_url} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-gray-800 hover:bg-[#00aaff] flex items-center justify-center transition-colors"><Twitter size={18} /></a>
              )}
            </div>
          </div>

          {/* Shop */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4">{t('footer.shop')}</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/collection?cat=optical" className="hover:text-[#00aaff] transition-colors">{t('footer.opticalGlasses')}</Link></li>
              <li><Link to="/collection?cat=sunglasses" className="hover:text-[#00aaff] transition-colors">{t('footer.solarGlasses')}</Link></li>
              <li><Link to="/collection?cat=camera" className="hover:text-[#00aaff] transition-colors">{t('footer.cameraAR')}</Link></li>
              <li><Link to="/collection" className="hover:text-[#00aaff] transition-colors">{t('footer.allCollection')}</Link></li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4">{t('footer.support')}</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/politique-retour" className="hover:text-[#00aaff] transition-colors">{t('footer.returnsRefunds')}</Link></li>
              <li><Link to="/cgv" className="hover:text-[#00aaff] transition-colors">{t('footer.salesConditions')}</Link></li>
              <li><Link to="/contact" className="hover:text-[#00aaff] transition-colors">{t('footer.contact')}</Link></li>
              <li><Link to="/blog" className="hover:text-[#00aaff] transition-colors">{t('footer.blog')}</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4">{t('footer.contactTitle')}</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center gap-2">
                <Mail size={16} className="shrink-0 text-[#00aaff]" />
                <a href={`mailto:${contactSettings.contact_email}`} className="hover:text-[#00aaff] transition-colors">{contactSettings.contact_email}</a>
              </li>
              <li className="flex items-center gap-2">
                <Phone size={16} className="shrink-0 text-[#00aaff]" />
                <a href={`tel:${contactSettings.contact_phone.replace(/\s/g, '')}`} className="hover:text-[#00aaff] transition-colors">{contactSettings.contact_phone}</a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-xs text-center md:text-left">
              &copy; {new Date().getFullYear()} VOYEZ VOUS. {t('footer.allRights')}
            </p>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs">
              <Link to="/mentions-legales" className="text-gray-400 hover:text-[#00aaff] transition-colors">{t('footer.legalNotices')}</Link>
              <Link to="/cgv" className="text-gray-400 hover:text-[#00aaff] transition-colors">{t('footer.cgv')}</Link>
              <Link to="/cgu" className="text-gray-400 hover:text-[#00aaff] transition-colors">{t('footer.cgu')}</Link>
              <Link to="/politique-confidentialite" className="text-gray-400 hover:text-[#00aaff] transition-colors">{t('footer.privacy')}</Link>
              <Link to="/politique-cookies" className="text-gray-400 hover:text-[#00aaff] transition-colors">{t('footer.cookies')}</Link>
              <button onClick={() => window.dispatchEvent(new CustomEvent('openCookieSettings'))} className="text-gray-400 hover:text-[#00aaff] transition-colors">{t('footer.manageCookies')}</button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
